import { useState, useEffect, useRef, useCallback } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import moodyDog from "../assets/moody_dog.json";
import apiService from "../services/api";

// iOS-matching taglines
const TAGLINES = [
  "Oh boy, that's a lot of stuff!",
  "Still counting... don't rush me!",
  "Almost done... probably!",
  "But wait, there's more!",
  "Your collection is MASSIVE!",
  "Who needs sleep when you have this much content?",
  "This is going to be epic!",
  "Loading all the good stuff...",
  "So many choices, so little time!",
  "Your couch is going to love you!",
  "Binge-watching paradise incoming...",
  "We're basically building a cinema here!",
  "Plot twist: there's even more!",
  "Your favorites just got dangerous!",
  "Professional couch potato mode: activated!",
];

const PHASES = {
  loading: "Gathering Media Information",
  liveTv: "Loading Live TV",
  movies: "Loading Movies",
  series: "Loading Series",
  total: "Total Media",
  caching: "Applying Data to EPG",
  done: "Setup Complete!",
};

const CACHING_MESSAGES = [
  "Teaching channels to line up nicely...",
  "Convincing the EPG data to cooperate...",
  "Sorting channels... alphabetically? Nah, by vibe.",
  "Herding thousands of channels into a grid...",
  "Asking each channel to introduce itself...",
  "The EPG is loading... it has a lot to say.",
  "Building your TV guide from scratch. No pressure.",
  "Almost there... the channels are taking attendance.",
  "Mapping out your entire entertainment universe...",
  "EPG data is fashionably late, as usual.",
  "Organizing more channels than your remote has buttons...",
  "The channels are arguing over who goes first...",
  "Hang tight, we're building something beautiful here.",
  "Your future binge sessions are being calculated...",
  "Loading so much content, even we're impressed.",
];

const TIMINGS = {
  TAGLINE_ROTATION_MS: 5000,
  TAGLINE_FADE_MS: 300,
  ANIMATION_STEP_MS: 5000,
  ANIMATION_FRAME_MS: 16,
  BACKDROP_ROTATION_MS: 5000,
  BACKDROP_CROSSFADE_MS: 1000,
  CACHING_PAUSE_MS: 2000,
  DONE_PAUSE_MS: 1500,
};

const formatCount = (num) => num.toLocaleString();

const WebPlayerSetup = () => {
  const navigate = useNavigate();
  const animationFrameRef = useRef(null);
  const taglineInterval = useRef(null);
  const backdropInterval = useRef(null);

  // Phase & count state (matching iOS)
  const [currentPhase, setCurrentPhase] = useState("loading");
  const [displayedCount, setDisplayedCount] = useState(0);
  const [countSpeed, setCountSpeed] = useState(0); // 0-1, drives motion blur

  // Tagline state with fade (matching iOS)
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [taglineOpacity, setTaglineOpacity] = useState(1);

  // Trending backdrop crossfade (matching iOS)
  const [currentBackdrop, setCurrentBackdrop] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  const [nextBackdrop, setNextBackdrop] = useState(null);
  const [nextLogo, setNextLogo] = useState(null);
  const [showNext, setShowNext] = useState(false);

  // Caching subtitle rotation
  const [cachingMessage, setCachingMessage] = useState("");
  const cachingMsgInterval = useRef(null);

  // Error state
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchTrending = useCallback(async () => {
    try {
      const response = await fetch("https://api.lunova.app/v2/login/trending", {
        headers: { Authorization: "Bearer NoAuth" },
      });
      const data = await response.json();

      if (data?.data) {
        return {
          backdrop: data.data.backdropPath,
          logo: data.data.logoPath,
        };
      }
    } catch {
      // Backdrop is non-critical
    }

    return null;
  }, []);

  // Tagline rotation with fade in/out (matching iOS 5s interval, 0.3s fade)
  useEffect(() => {
    taglineInterval.current = setInterval(() => {
      // Fade out
      setTaglineOpacity(0);

      // After fade out, change tagline and fade in
      setTimeout(() => {
        setTaglineIndex((prev) => (prev + 1) % TAGLINES.length);
        setTaglineOpacity(1);
      }, TIMINGS.TAGLINE_FADE_MS);
    }, TIMINGS.TAGLINE_ROTATION_MS);

    return () => {
      if (taglineInterval.current) clearInterval(taglineInterval.current);
    };
  }, []);

  // Trending backdrop rotation with crossfade (matching iOS)
  useEffect(() => {
    let mounted = true;

    const initBackdrop = async () => {
      const item = await fetchTrending();

      if (item && mounted) {
        setCurrentBackdrop(item.backdrop);
        setCurrentLogo(item.logo);
      }
    };

    initBackdrop();

    backdropInterval.current = setInterval(async () => {
      const item = await fetchTrending();

      if (item && mounted) {
        setNextBackdrop(item.backdrop);
        setNextLogo(item.logo);
        setShowNext(true);

        // After crossfade completes, swap
        setTimeout(() => {
          if (mounted) {
            setCurrentBackdrop(item.backdrop);
            setCurrentLogo(item.logo);
            setShowNext(false);
            setNextBackdrop(null);
            setNextLogo(null);
          }
        }, TIMINGS.BACKDROP_CROSSFADE_MS + 200);
      }
    }, TIMINGS.BACKDROP_ROTATION_MS);

    return () => {
      mounted = false;
      if (backdropInterval.current) clearInterval(backdropInterval.current);
    };
  }, [fetchTrending]);

  // Animate a single phase counter with easeOut curve and motion blur tracking
  const animatePhase = useCallback(
    (phase, target) =>
      new Promise((resolve) => {
        setCurrentPhase(phase);
        setDisplayedCount(0);
        setCountSpeed(1); // Start at full speed

        if (target === 0) {
          setDisplayedCount(0);
          setCountSpeed(0);
          resolve();

          return;
        }

        const startTime = performance.now();
        const tick = () => {
          const elapsed = performance.now() - startTime;
          const linearProgress = Math.min(
            elapsed / TIMINGS.ANIMATION_STEP_MS,
            1
          );
          // easeOutCubic: fast start, slow finish — makes the blur feel natural
          const eased = 1 - Math.pow(1 - linearProgress, 3);
          const value = Math.floor(target * eased);
          const speed = 1 - linearProgress; // Velocity decreases linearly

          setDisplayedCount(value);
          setCountSpeed(speed);

          if (linearProgress < 1) {
            animationFrameRef.current = requestAnimationFrame(tick);
          } else {
            setDisplayedCount(target);
            setCountSpeed(0);
            resolve();
          }
        };

        animationFrameRef.current = requestAnimationFrame(tick);
      }),
    []
  );

  // Main setup flow (matching iOS sequential phases)
  const initSetup = useCallback(async () => {
    try {
      const response = await apiService.webPlayerSetupStepOne();

      const counts = response?.data || response;
      const liveTv = counts?.liveTv ?? 0;
      const movies = counts?.movies ?? 0;
      const series = counts?.series ?? 0;
      const total = counts?.total ?? 0;

      if (
        typeof liveTv !== "number" ||
        typeof movies !== "number" ||
        typeof series !== "number" ||
        typeof total !== "number"
      ) {
        toast.error("Invalid setup data received!");
        navigate("/web-player/profiles");

        return;
      }

      // Start cache in background — navigation waits for this to finish
      const cachePromise = apiService.webPlayerSetupStepTwo().catch((err) => {
        console.error("Cache data error:", err);
      });

      // Animate phases sequentially (eye candy while cache runs)
      const animationPromise = (async () => {
        await animatePhase("liveTv", liveTv);
        await animatePhase("movies", movies);
        await animatePhase("series", series);
        await animatePhase("total", total);

        // Caching phase — start rotating funny messages
        setCurrentPhase("caching");
        setDisplayedCount(0);
        let msgIndex = 0;

        setCachingMessage(CACHING_MESSAGES[0]);
        cachingMsgInterval.current = setInterval(() => {
          msgIndex = (msgIndex + 1) % CACHING_MESSAGES.length;
          setCachingMessage(CACHING_MESSAGES[msgIndex]);
        }, 3000);
      })();

      // Wait for BOTH cache API and animations to complete before navigating
      await Promise.all([cachePromise, animationPromise]);

      // Stop caching messages
      if (cachingMsgInterval.current) clearInterval(cachingMsgInterval.current);

      // Done phase (matching Android/iOS 1.5s)
      setCurrentPhase("done");
      await new Promise((r) => setTimeout(r, TIMINGS.DONE_PAUSE_MS));

      // Navigate — content is now fully loaded
      toast.success("Setup successfully completed!");
      navigate("/web-player/profiles");
    } catch (error) {
      console.error("Setup initialization error:", error);
      setErrorMessage("Setup failed. Continuing...");
      await new Promise((r) => setTimeout(r, 2000));
      navigate("/web-player/profiles");
    }
  }, [animatePhase, navigate]);

  useEffect(() => {
    initSetup();

    return () => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      if (cachingMsgInterval.current) clearInterval(cachingMsgInterval.current);
    };
  }, [initSetup]);

  const showCount =
    currentPhase !== "loading" &&
    currentPhase !== "caching" &&
    currentPhase !== "done";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden">
      {/* Trending backdrop layer with crossfade (matching iOS) */}
      <div className="absolute inset-0 z-0">
        {/* Current backdrop */}
        {currentBackdrop && (
          <motion.img
            alt=""
            animate={{ opacity: showNext ? 0 : 1 }}
            className="absolute inset-0 w-full h-full object-cover"
            src={currentBackdrop}
            style={{ opacity: 0.3 }}
            transition={{ duration: TIMINGS.BACKDROP_CROSSFADE_MS / 1000 }}
          />
        )}
        {/* Next backdrop (crossfade in) */}
        {nextBackdrop && (
          <motion.img
            alt=""
            animate={{ opacity: showNext ? 1 : 0 }}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            src={nextBackdrop}
            style={{ opacity: 0.3 }}
            transition={{ duration: TIMINGS.BACKDROP_CROSSFADE_MS / 1000 }}
          />
        )}
        {/* Dark gradient overlay for readability (matching iOS) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.2) 100%)",
          }}
        />
      </div>

      {/* Main setup content */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="text-center z-10 flex flex-col items-center"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        {/* Lottie animation - Moody Dog (matching iOS) */}
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="flex justify-center mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="w-64 h-64">
            <Lottie animationData={moodyDog} loop={true} />
          </div>
        </motion.div>

        {/* Phase label (matching iOS) */}
        <motion.h1
          animate={{ opacity: 1 }}
          className="text-3xl font-bold text-white mb-3 drop-shadow-lg"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {PHASES[currentPhase]}
        </motion.h1>

        {/* Animated count number with vertical motion blur */}
        {showCount && (
          <AnimatePresence mode="popLayout">
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="relative mb-3"
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 1.05 }}
              key={`count-${currentPhase}`}
              transition={{ duration: 0.1 }}
            >
              {/* Ghost trails above and below for motion blur effect */}
              {countSpeed > 0.05 && (
                <>
                  <span
                    className="absolute inset-0 flex items-center justify-center font-black select-none"
                    style={{
                      color: "rgb(100, 148, 237)",
                      fontSize: "3.5rem",
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.1,
                      transform: `translateY(${-8 * countSpeed}px)`,
                      opacity: 0.3 * countSpeed,
                      filter: `blur(${3 * countSpeed}px)`,
                    }}
                  >
                    {formatCount(displayedCount)}
                  </span>
                  <span
                    className="absolute inset-0 flex items-center justify-center font-black select-none"
                    style={{
                      color: "rgb(100, 148, 237)",
                      fontSize: "3.5rem",
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.1,
                      transform: `translateY(${8 * countSpeed}px)`,
                      opacity: 0.3 * countSpeed,
                      filter: `blur(${3 * countSpeed}px)`,
                    }}
                  >
                    {formatCount(displayedCount)}
                  </span>
                  <span
                    className="absolute inset-0 flex items-center justify-center font-black select-none"
                    style={{
                      color: "rgb(100, 148, 237)",
                      fontSize: "3.5rem",
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.1,
                      transform: `translateY(${-16 * countSpeed}px)`,
                      opacity: 0.15 * countSpeed,
                      filter: `blur(${6 * countSpeed}px)`,
                    }}
                  >
                    {formatCount(displayedCount)}
                  </span>
                  <span
                    className="absolute inset-0 flex items-center justify-center font-black select-none"
                    style={{
                      color: "rgb(100, 148, 237)",
                      fontSize: "3.5rem",
                      fontVariantNumeric: "tabular-nums",
                      lineHeight: 1.1,
                      transform: `translateY(${16 * countSpeed}px)`,
                      opacity: 0.15 * countSpeed,
                      filter: `blur(${6 * countSpeed}px)`,
                    }}
                  >
                    {formatCount(displayedCount)}
                  </span>
                </>
              )}
              {/* Main number - slight blur when fast */}
              <p
                className="font-black drop-shadow-lg"
                style={{
                  color: "rgb(100, 148, 237)",
                  fontSize: "3.5rem",
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.1,
                  filter:
                    countSpeed > 0.05 ? `blur(${1.5 * countSpeed}px)` : "none",
                }}
              >
                {formatCount(displayedCount)}
              </p>
            </motion.div>
          </AnimatePresence>
        )}

        {/* Rotating caching subtitle while waiting on EPG */}
        {currentPhase === "caching" && cachingMessage && (
          <AnimatePresence mode="wait">
            <motion.p
              animate={{ opacity: 0.7, y: 0 }}
              className="text-lg text-gray-300 mt-2 mb-3 px-10"
              exit={{ opacity: 0, y: -8 }}
              initial={{ opacity: 0, y: 8 }}
              key={cachingMessage}
              transition={{ duration: 0.3 }}
            >
              {cachingMessage}
            </motion.p>
          </AnimatePresence>
        )}

        {/* "Get Ready!" subtitle on done phase */}
        {currentPhase === "done" && (
          <motion.p
            animate={{ opacity: 0.7 }}
            className="text-xl font-medium text-white mb-3"
            initial={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            Get Ready!
          </motion.p>
        )}

        {/* Rotating tagline with fade (matching iOS 5s interval, 0.3s ease) */}
        <motion.p
          animate={{ opacity: taglineOpacity * 0.6 }}
          className="text-lg italic text-white drop-shadow-md mt-6 px-10"
          transition={{ duration: TIMINGS.TAGLINE_FADE_MS / 1000 }}
        >
          {TAGLINES[taglineIndex]}
        </motion.p>

        {/* Error message */}
        {errorMessage && (
          <motion.p
            animate={{ opacity: 1 }}
            className="text-sm text-red-400 mt-4"
            initial={{ opacity: 0 }}
          >
            {errorMessage}
          </motion.p>
        )}
      </motion.div>

      {/* Trending logo - bottom left (matching iOS) */}
      <AnimatePresence mode="wait">
        {(showNext ? nextLogo : currentLogo) && (
          <motion.div
            animate={{ opacity: 0.7 }}
            className="absolute bottom-8 left-8 z-10"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            key={showNext ? nextLogo : currentLogo}
            transition={{ duration: TIMINGS.BACKDROP_CROSSFADE_MS / 1000 }}
          >
            <img
              alt=""
              className="object-contain"
              src={showNext ? nextLogo : currentLogo}
              style={{ maxWidth: "180px", maxHeight: "80px" }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WebPlayerSetup;
